import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import styles from './app.module.scss';
import Header from './components/header/header.component';
import Footer from './components/footer/footer.component';
import PodcastBlock from './components/podcast-block/podcast-block.component';
import FrontPage from './components/front-page/front-page.component';
import Popup from './components/popup/popup.component';
import ArticlePagination from './components/article-pagination/article-pagination.component';
import Cursor from './components/cursor/cursor.component';
import PageAppearEffect from './components/page-appear-effect/page-appear-effect.component';
import { ArticleIdsEnum, ArticleInterface, ARTICLES, ArticleThemesEnum } from './data/articles';
import ArticleBlock from './components/article-block/article-block.component';
import { PERSONS } from './data/persons';
import { QuoteInterface as QuoteComponentInterface } from './components/quote/quote.interface';
import Credits from './components/credits/credits.component';
import { QuoteInterface } from './data/quotes';
import { cursorRootEl, openedArticlePrefix, podcastsPrefix } from './constants';

const parseArticleQuote = (data: QuoteInterface): QuoteComponentInterface => {
  return {
    author: `${PERSONS[data.author].name}, ${PERSONS[data.author].company || PERSONS[data.author].position.join(', ')}`,
    text: data.text,
  };
};

const themeClassNames: Record<ArticleThemesEnum, string> = {
  [ArticleThemesEnum.GREEN]: styles.podcastBlockGreen,
  [ArticleThemesEnum.GRAY]: styles.podcastBlockGray,
  [ArticleThemesEnum.BLUE]: styles.podcastBlockBlue
};

const App: React.FunctionComponent = () => {
  const hash = window.location.hash.slice(1);

  const [openedPopup, setOpenedPopup] = useState<ArticleIdsEnum | null>(
    Object.values(ARTICLES).find(
      thisItem => hash === `${openedArticlePrefix}${thisItem.id}`,
    )?.id
    || null,
  );

  useEffect(() => {
    setTimeout(() => document.body.setAttribute('data-ready', ''), 700);

    return (
      ReactDOM.render(
        <Cursor />,
        cursorRootEl,
      )
    );
  }, []);

  const openedArticle: ArticleInterface | null = openedPopup !== null
    ? ARTICLES[openedPopup]
    : null;

  if (openedArticle) {
    window.history.replaceState(null, '',
      `#${openedArticlePrefix}${openedArticle.id}`,
    );
  } else if ((new RegExp(`^#${openedArticlePrefix}`)).test(window.location.hash)) {
    window.history.replaceState(null, '',
      ' ',
    );
  }

  const handlePodcastOpen = ({
    id,
    isOpened,
  }: {
    id: string;
    isOpened: boolean;
  }) => {
    if (isOpened) {
      window.history.replaceState(null, '',
        `#${podcastsPrefix}${id}`,
      );
    } else if ((new RegExp(`^#${podcastsPrefix}${id}`)).test(window.location.hash)) {
      window.history.replaceState(null, '',
        ' ',
      );
    }
  }

  const previewList = [
    ARTICLES[ArticleIdsEnum.growthPoints],
    ARTICLES[ArticleIdsEnum.marketing],
    ARTICLES[ArticleIdsEnum.ecosystem],
  ].map(
    thisItem => (
      <PodcastBlock
        key={thisItem.id}
        id={thisItem.id}
        className={classNames(
          styles.podcastBlock,
          themeClassNames[thisItem.theme],
        )}
        leftPhoto={thisItem.speakers.left.maulImageUrl}
        rightPhoto={thisItem.speakers.right.maulImageUrl}
        articleData={{
          title: thisItem.title,
          subTitle: thisItem.subtitle,
          personLeft: thisItem.speakers.left,
          personRight: thisItem.speakers.right,
          video: thisItem.video,
          quoteMain: parseArticleQuote(thisItem.quotes.top),
          quoteFirst: parseArticleQuote(thisItem.quotes.right),
          quoteSecond: parseArticleQuote(thisItem.quotes.left),
          podcastLinks: thisItem.podcastLinks,
        }}
        onReadArticleClick={() => setOpenedPopup(thisItem.id)}
        onPodcastOpenChange={(isOpened) => handlePodcastOpen({
          id: thisItem.id,
          isOpened,
        })}
      />
    ),
  );

  return (
    <div
      className={classNames(
        styles.component,
        styles.isPopupOpened,
      )}
      onMouseOver={(event) => {
        // @ts-ignore
        if (event.target?.tagName === 'IFRAME') {
          document.body.setAttribute('data-iframe-hover', '');

          return;
        }

        document.body.removeAttribute('data-iframe-hover');
      }}
    >
      <PageAppearEffect />

      <Header />

      <FrontPage />

      {previewList}

      <section>
        {Object.values(ARTICLES).map(
          thisItem => (
            <Popup
              key={thisItem.id}
              isOpened={openedArticle?.id === thisItem.id}
              className={themeClassNames[thisItem.theme]}
              onClose={() => setOpenedPopup(null)}
              // className={thisItem.className}
              footer={(
                <ArticlePagination
                  prevArticle={{
                    imageUrl: ARTICLES[thisItem.prev].speakers.previewImage,
                    children: ARTICLES[thisItem.prev].title,
                  }}
                  nextArticle={{
                    imageUrl: ARTICLES[thisItem.next].speakers.previewImage,
                    children: ARTICLES[thisItem.next].title,
                  }}
                  onPrev={() => setOpenedPopup(thisItem.prev)}
                  onNext={() => setOpenedPopup(thisItem.next)}
                />
              )}
            >
              <ArticleBlock
                title={thisItem.title}
                content={thisItem.content}
              />
            </Popup>
          ),
        )}
      </section>

      <Footer />
      <Credits />
    </div>
  );
};

export default App;
